import { tokenManager } from '../utils/api';
import { USER_URL } from '../config/environment';
import { URL_SHORTEN } from '../config/environment';
import axios from 'axios';

/**
 * URL Management Service
 * Handles all URL-related API calls
 */

export const urlService = {
  /**
   * Shorten URL
   * @param {Object} urlData - URL data
   * @returns {Promise<Object>} Shortened URL data
   */
  shortenUrl: async (urlData) => {
    const response = await axios.post(`${URL_SHORTEN}/api/url/jwt-shorten`, urlData, {
      headers: { 
        Authorization: `Bearer ${tokenManager.getAccessToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  /**
   * Get URL mappings by account
   * @param {string} accountId - Account ID
   * @param {number} page - Page number
   * @param {number} size - Page size
   * @param {string} fromDate - Start date (optional, format: YYYY-MM-DD)
   * @param {string} toDate - End date (optional, format: YYYY-MM-DD)
   * @returns {Promise<Object>} URL mappings with pagination
   */
  getUrlMappingsByAccount: async (accountId, page = 0, size = 10, fromDate = null, toDate = null) => {
    const params = { 
      accountId,
      searchTerm: '', 
      page, 
      size 
    };
    
    if (fromDate && fromDate !== '') {
      params.fromDate = fromDate;
    }
    if (toDate && toDate !== '') {
      params.toDate = toDate;
    }

    const response = await axios.get(`${USER_URL}/api/url-mappings/search`, {
      params,
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Search URL mappings
   * @param {Object|string} params - Either an object with search parameters or accountId string
   * @param {string} searchTerm - Search term (if params is string)
   * @param {number} page - Page number (if params is string)
   * @param {number} size - Page size (if params is string)
   * @param {string} fromDate - Start date (optional, if params is string)
   * @param {string} toDate - End date (optional, if params is string)
   * @returns {Promise<Object>} Search results with pagination
   */
  searchUrlMappings: async (params, searchTerm = '', page = 0, size = 10, fromDate = null, toDate = null) => {
    let requestPayload = {};
    
    // Handle both object and individual parameter formats
    if (typeof params === 'object' && params !== null) {
      // Object format - used by Reports.jsx (POST with JSON payload)
      requestPayload = {
        accountId: params.accountId,
        campaignName: params.campaignName || "",
        senderId: params.senderId || "",
        individualId: params.individualId || "",
        fromDate: params.fromDate || "",
        toDate: params.toDate || ""
      };
    } else {
      // Individual parameter format - legacy support (GET with query params)
      const queryParams = {
        accountId: params,
        searchTerm: searchTerm || '',
        page,
        size,
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate })
      };


      const response = await axios.get(`${USER_URL}/api/url-mappings/search`, {
        params: queryParams,
        headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
      });
      return response.data;
    }


    const response = await axios.post(`${USER_URL}/api/url-mappings/search`, requestPayload, {
      headers: { 
        Authorization: `Bearer ${tokenManager.getAccessToken()}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  /**
   * Get URL mappings for one month by default (without searchTerm)
   * @param {string} accountId - Account ID
   * @param {number} page - Page number (default: 0)
   * @param {number} size - Page size (default: 100)
   * @param {string} fromDate - Start date (default: first day of current month)
   * @param {string} toDate - End date (default: last day of current month)
   * @returns {Promise<Object>} URL mappings with pagination
   */
  getUrlMappingsOneMonth: async (accountId, page = 0, size = 10, fromDate = null, toDate = null) => {
    // Set default dates to current month if not provided
    const now = new Date();
    const defaultFromDate = fromDate || new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const defaultToDate = toDate || new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

    const queryParams = {
      accountId,
      page,
      size,
      fromDate: defaultFromDate,
      toDate: defaultToDate
    };

    const response = await axios.get(`${USER_URL}/api/url-mappings/search`, {
      params: queryParams,
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Delete URL mapping
   * @param {string} mappingId - URL mapping ID
   * @returns {Promise<Object>} Deletion response
   */
  deleteUrlMapping: async (mappingId) => {
    const response = await axios.delete(`${USER_URL}/api/url-mappings/delete/${mappingId}`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Export URL mappings
   * @param {Object|string} params - Either an object with export parameters or accountId string
   * @param {string} searchTerm - Search term (if params is string, legacy support)
   * @param {string} fromDate - Start date (if params is string, legacy support)
   * @param {string} toDate - End date (if params is string, legacy support)
   * @param {Function} onDownloadProgress - Progress callback (optional)
   * @returns {Promise<Blob>} Export file blob
   */
  exportUrlMappings: async (params, searchTerm = '', fromDate = '', toDate = '', onDownloadProgress = null) => {
    let requestPayload = {};
    
    // Handle both object and individual parameter formats
    if (typeof params === 'object' && params !== null) {
      // Object format - new POST with JSON payload
      requestPayload = {
        accountId: params.accountId,
        campaignName: params.campaignName || "",
        senderId: params.senderId || "",
        individualId: params.individualId || "",
        fromDate: params.fromDate || "",
        toDate: params.toDate || ""
      };
      
      
      const response = await axios({
        method: 'post',
        url: `${USER_URL}/api/url-mappings/export`,
        data: requestPayload,
        headers: {
          'Authorization': `Bearer ${tokenManager.getAccessToken()}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob',
        onDownloadProgress
      });
      return response.data;
    } else {
      // Individual parameter format - legacy support (GET with query params)
      
      const response = await axios({
        method: 'get',
        url: `${USER_URL}/api/url-mappings/export`,
        params: {
          accountId: params,
          searchTerm,
          fromDate,
          toDate
        },
        headers: {
          'Authorization': `Bearer ${tokenManager.getAccessToken()}`,
        },
        responseType: 'blob',
        onDownloadProgress
      });
      return response.data;
    }
  }
};

export default urlService;
